import {
    IResponse,
    MinOrganisationDto,
    ProtoUser,
    RequestAccessDto,
    spError,
} from '@eventr/common'
import { jwtDecode } from 'jwt-decode'
import { createContext, useCallback, useContext } from 'react'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { AdminAPI } from '../../data/adminCC'
import { EventrGatewayAPI } from '../../data/gateway.api'
import { ICompleteProfileDTO, UserAPI } from '../../data/User'
import { bs, useBoundStore } from '../../state/bound.store'
import { defaultAddSnack } from '../../state/overlayState/overlayContext'
import { loadingSignal } from '../../state/overlayState/overlaySignals'
import { logEvent } from '../firebase/firebase'
import { getMediaSizes } from '../media/media.slice'

interface IInviteUserData {
    flow_type?: 'invite' | 'create' | 'inviteLink' | 'guest'
    wp_id?: number
    marketingOptIn: boolean // Ensure this is always a boolean
    email?: string
    wp_token?: string
    firstName?: string
    lastName?: string
    phoneNumber?: string
    countryCode?: string
    password?: string
    org?: string
    profilePicFile?: File
    matchingOrgs?: MinOrganisationDto[]
    notSameUser?: boolean
    ignoreUserMismatch?: boolean
    itinCode?: string
    invitedBy?: string
    sharingPreference?: 'none' | 'all' | 'some'
    sectionsToInclude?: number[]
    isEIDP?: boolean
    authTokens?: {
        IdToken: string
        refreshToken: string
        expiration: number
    }
}

interface IRegistrationProviderContext {
    completeInviteRegistration?: () => Promise<void>
    getMatchingOrgs?: (
        email: string
    ) => Promise<IResponse<MinOrganisationDto[]>>
    requestOrgAccess?: (body: RequestAccessDto) => Promise<any>
    requestItineraryAccess?: (body: RequestAccessDto) => Promise<any>
    confirmInvite: (
        orgId: string,
        inviteToken: string
    ) => Promise<IResponse<any>>
    setCurrentUserAsInviteUser: () => void
    createAccount?: (body: ProtoUser) => Promise<any>
    consumeLinkData?: (code: string) => Promise<void>
}

const RegistrationProviderContext = createContext<IRegistrationProviderContext>(
    {
        completeInviteRegistration: undefined,
        getMatchingOrgs: undefined,
        requestOrgAccess: undefined,
        requestItineraryAccess: undefined,
        confirmInvite: async () => {
            return Promise.resolve({ status: 'error', message: '' })
        },
        setCurrentUserAsInviteUser: () => {},
        createAccount: async () => {
            return Promise.resolve({ status: 'error', message: '' })
        },
        consumeLinkData: async () => {
            return
        },
    }
)
interface IRegistrationStateContext {
    inviteUserData: IInviteUserData
    setInviteData: (data: Partial<IInviteUserData>) => void
    itinName?: string
    setItinName: (name: string) => void
}

export const useRegStore = create<IRegistrationStateContext>((set) => ({
    inviteUserData: {
        marketingOptIn: false, // Explicitly set as boolean
    },
    setInviteData: (data: Partial<IInviteUserData>) => {
        set((state) => {
            return {
                inviteUserData: {
                    ...state.inviteUserData,
                    ...data,
                    marketingOptIn:
                        data.marketingOptIn ??
                        state.inviteUserData?.marketingOptIn ??
                        false,
                },
            }
        })
    },

    itinName: '',
    setItinName: (name: string) => {
        set((state) => {
            return {
                itinName: name,
            }
        })
    },
}))
export const RegistrationProvider = (props: { children: React.ReactNode }) => {
    const { profile } = useBoundStore(
        useShallow((s) => {
            return {
                profile: s.profile,
            }
        })
    )
    const { inviteUserData, setInviteData } = useRegStore((s) => {
        return {
            inviteUserData: s.inviteUserData,
            setInviteData: s.setInviteData,
        }
    })
    const { setItinName } = useRegStore((s) => {
        return {
            initName: s.itinName,
            setItinName: s.setItinName,
        }
    })

    const completeInviteRegistration = async () => {
        loadingSignal.value = true
        if (!inviteUserData) {
            return
        }

        if (!inviteUserData.wp_id) {
            defaultAddSnack('User Id is Required', 'error')
            loadingSignal.value = false
            return
        }

        const completeDTO: ICompleteProfileDTO = {
            firstName: inviteUserData.firstName,
            lastName: inviteUserData.lastName,
            phoneNumber: inviteUserData.phoneNumber,
            password: inviteUserData.password,
            id: inviteUserData.wp_id,
            marketingOptIn: inviteUserData.marketingOptIn,
            countryCode: inviteUserData.countryCode,
            globalId:
                inviteUserData.authTokens !== undefined
                    ? jwtDecode(inviteUserData.authTokens.IdToken).sub
                    : undefined,
            termsAccepted: true,
        }
        //complete user profile in wp this will also create a user in the profile and keycloak
        const userCompletion = await UserAPI.completeProfile(completeDTO)
        if (userCompletion.status === 'error') {
            logEvent('sign_up_complete_failed', {
                message: userCompletion.message,
            })
            defaultAddSnack("Couldn't complete registration", 'error')
            spError(
                'Error completing profile',
                'completeInviteRegistration',
                userCompletion.message
            )
            return
        }
        let loginResp
        if (inviteUserData.isEIDP) {
            bs().setTokens(inviteUserData.authTokens)
            loginResp = {
                status: 'success',
                data: {
                    access_token: inviteUserData.authTokens?.IdToken ?? '',
                },
            }
            await bs().onLogin()
        } else {
            // run login request to keycloak
            loginResp = await bs().signInWithPassword(
                inviteUserData.email ?? '',
                inviteUserData.password ?? '',
                null
            )
        }
        if (loginResp.status === 'error') {
            logEvent('sign_up_complete_failed', {
                message: loginResp.message,
            })
            defaultAddSnack(
                "Couldn't login into your account please try using sing in screen",
                'error'
            )
            spError('Error singin ', 'signInWithPassword', loginResp.message)
            return
        }

        if (!inviteUserData.profilePicFile) {
            logEvent('sign_up_completed', {})
            window.pushPath('/')
            return
        }

        try {
            await uploadProfilePic(inviteUserData.profilePicFile)
        } catch (error) {
            logEvent('sign_up_profile_pic_failed', {
                message:
                    error instanceof Error ? error.message : 'Unknown error',
            })
            spError('Error uploading media ', 'uploadMedia', error)
            loadingSignal.value = false
            logEvent('sign_up_completed', {})
            window.pushPath('/')
        }
        //upload the profile pic
    }
    const uploadProfilePic = async (file: File) => {
        if (!inviteUserData?.wp_id) return

        const picResp = await bs().uploadMedia(
            file,
            'ProfilePic',
            inviteUserData?.org
        )
        if (picResp.status === 'error' || picResp.data === undefined) {
            defaultAddSnack(
                'Failed to upload profile pic please try using account screen',
                'error'
            )
            logEvent('sign_up_profile_pic_failed', {
                message: picResp.message,
            })
            spError('Error uploading media ', 'uploadMedia', picResp.message)
            logEvent('sign_up_completed', {})
            window.pushPath('/')
            return
        }
        //and set that as the user profile pic
        await UserAPI.updateProfile({
            id: inviteUserData.wp_id,
            profilePicUrl: picResp?.data.source_url,
            profilePicSizes: getMediaSizes(picResp.data),
        })
        //now we can redirect to the dashboard
        logEvent('sign_up_completed', {})
        window.pushPath('/')
    }

    const setCurrentUserAsInviteUser = () => {
        setInviteData({
            marketingOptIn: false,
            email: profile?.email.toLowerCase(),
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            phoneNumber: profile?.phoneNumber,
            password: '',
        })
    }

    const getMatchingOrgs = async (email: string) => {
        const domain = email.substring(email.lastIndexOf('@') + 1).toLowerCase()
        const domainInList = await AdminAPI.domainInList(domain)
        if (domainInList) {
            return Promise.resolve<IResponse<MinOrganisationDto[]>>({
                status: 'success',
                data: [],
            })
        }

        const returnData = await AdminAPI.matchOrgV2(domain)
        return returnData
    }

    const requestOrgAccess = async (body: RequestAccessDto) => {
        return await EventrGatewayAPI.requestAccess.request(body)
    }

    const requestItineraryAccess = async (body: RequestAccessDto) => {
        return await EventrGatewayAPI.requestAccess.requestItinAccess(body)
    }

    const createAccount = async (body: ProtoUser) => {
        return await EventrGatewayAPI.profile.createUser(body)
    }
    const confirmInvite = useCallback(
        async (orgId: string, inviteToken: string) => {
            return await EventrGatewayAPI.registration.confirmInvite(
                orgId,
                inviteToken
            )
        },
        []
    )

    const consumeLinkData = async (encoded: string) => {
        loadingSignal.value = true
        try {
            if (encoded) {
                const decoded = atob(encoded)
                const itinCode = decoded.split('_')[0]
                const codeInfo = await EventrGatewayAPI.orgs.getCodeInfo(
                    itinCode,
                    true
                )

                if (codeInfo.status === 'error' || codeInfo.data.length < 1) {
                    throw new Error('Invalid code')
                }
                let orgId =
                    codeInfo.data[0].organisation._id ??
                    codeInfo.data[0].organisation

                let friendlyName =
                    codeInfo.data[0].organisation.friendlyName ??
                    codeInfo.data[0].organisation.split('_')[0]

                const shareCodeInfo = await EventrGatewayAPI.orgs.getShareCode(
                    encoded,
                    orgId
                )
                if (
                    shareCodeInfo.status === 'error' ||
                    codeInfo.data.length < 1
                ) {
                    throw new Error('Invalid code')
                }

                //could do with friendly name here as well
                setInviteData({
                    ignoreUserMismatch: true,
                    flow_type: 'inviteLink',
                    itinCode: codeInfo.data[0].itineraryId,
                    org: orgId,
                    matchingOrgs: [
                        {
                            _id: orgId,
                            friendlyName: friendlyName,
                        },
                    ],
                    invitedBy: shareCodeInfo.data?.userID,
                    sharingPreference:
                        shareCodeInfo.data?.sharingPreference || 'none',
                    sectionsToInclude:
                        shareCodeInfo.data?.sectionsToInclude || [],
                })

                if (codeInfo.data[0].itineraryInfo.name) {
                    setItinName(codeInfo.data[0].itineraryInfo.name)
                }

                if (bs().profile) {
                    setCurrentUserAsInviteUser()

                    window.pushPath('/authentication/requestAccess')
                }
            }
        } catch (error) {
            defaultAddSnack(
                "Couldn't consume link please try again later",
                'error'
            )
            spError(
                'Error ',
                'consumeLinkData',
                error instanceof Error ? error.message : 'Unknown error'
            )
            window.pushPath('/')
        } finally {
            loadingSignal.value = false
        }
    }

    return (
        <RegistrationProviderContext.Provider
            value={{
                completeInviteRegistration,
                setCurrentUserAsInviteUser,
                getMatchingOrgs,
                requestOrgAccess,
                requestItineraryAccess,
                confirmInvite,
                createAccount,
                consumeLinkData,
            }}
        >
            {props.children}
        </RegistrationProviderContext.Provider>
    )
}
export const useRegistrationContext = () => {
    return useContext(RegistrationProviderContext)
}
